/**
 * Font Loader — loads local TTF fonts into pdfmake's Virtual File System.
 *
 * Scans Quill-generated HTML for `ql-font-*` CSS classes, then loads the
 * corresponding TTF files from `/fonts/ttf/` and registers them in pdfmake's
 * VFS so they render correctly in generated PDFs.
 *
 * Font loading tiers:
 *   1. Standard PDF fonts (Helvetica) — loaded from pdfmake's bundled AFM modules
 *   2. Local TTF fonts — fetched from `/fonts/ttf/` and registered in pdfmake VFS
 *
 * Loaded fonts are cached in memory for the session — once a font is loaded,
 * subsequent calls are no-ops.
 */

import type { TVirtualFileSystem, TFontDictionary } from 'pdfmake/interfaces';
import { FONT_REGISTRY, FALLBACK_FONT_ENTRY, type FontRegistryEntry } from './fontRegistry';
import { getPdfMake, loadPdfMake } from '../pdfmakeLoader';

// ── Session cache ────────────────────────────────────────────────────────────

/** Tracks in-flight and completed font loads to prevent duplicate work */
const fontPromises = new Map<string, Promise<void>>();

/** Tracks fonts that were successfully loaded (promise resolved, VFS registered) */
const loadedFonts = new Set<string>();

/** Cached character maps (codepoint sets) for loaded fonts, keyed by pdfmake name */
const fontCmaps = new Map<string, Set<number>>();

/** The pdfmake font name for the Unicode symbol fallback font */
export const FALLBACK_FONT = 'NotoSansSymbols2';

/** Promise for the in-flight or completed fallback font load */
let fallbackFontPromise: Promise<void> | null = null;

/** Promise for the one-time extraction of Roboto's cmap from pdfmake's VFS */
let robotoCmapPromise: Promise<void> | null = null;

// ── Public API ───────────────────────────────────────────────────────────────

/**
 * Scan HTML for `ql-font-*` CSS classes and return the unique Quill font IDs found.
 */
export function scanHtmlForFonts(html: string): string[] {
    if (!html) return [];
    const matches = html.matchAll(/ql-font-(\w+)/g);
    const ids = new Set<string>();
    for (const m of matches) {
        ids.add(m[1]);
    }
    return [...ids];
}

/**
 * Main entry point: scan HTML for custom fonts and load any that haven't
 * been registered yet. Uses `Promise.allSettled` so one failed font
 * won't block the others.
 *
 * Extracts Roboto's cmap on first call so it can participate in the
 * fallback chain. The symbol fallback font (Noto Sans Symbols 2) is
 * loaded lazily — only when the HTML contains characters not covered
 * by the primary font or Roboto.
 *
 * Call this before `parseQuillHtml()` to ensure fonts are available.
 */
export async function loadFontsForPdf(html: string): Promise<void> {
    // Extract Roboto's cmap from pdfmake's bundled VFS (one-time)
    await ensureRobotoCmap();

    const fontIds = scanHtmlForFonts(html);

    const pending: Promise<void>[] = [];

    // Load custom fonts found in the HTML
    for (const id of fontIds) {
        if (fontPromises.has(id)) {
            pending.push(fontPromises.get(id)!);
            continue;
        }
        const entry = FONT_REGISTRY.get(id);
        if (!entry) {
            console.warn(`[fontLoader] Unknown font ID: ${id}`);
            continue;
        }
        const promise = loadFont(entry);
        fontPromises.set(id, promise);
        pending.push(promise);
    }

    if (pending.length > 0) {
        const results = await Promise.allSettled(pending);
        for (const result of results) {
            if (result.status === 'rejected') {
                console.warn('[fontLoader] Font load failed:', result.reason);
            }
        }
    }

    // After custom fonts are loaded, check if the HTML text has characters
    // not covered by any loaded font + Roboto — only then load the fallback
    if (fallbackFontPromise || textNeedsFallbackFont(html)) {
        if (!fallbackFontPromise) {
            fallbackFontPromise = loadFont(FALLBACK_FONT_ENTRY);
        }
        try {
            await fallbackFontPromise;
        } catch (e) {
            console.warn('[fontLoader] Fallback font load failed:', e);
        }
    }
}

/**
 * Get the pdfmake font name for a Quill font ID.
 * Returns `'Roboto'` if the font isn't registered or failed to load.
 */
export function getFontName(quillId: string): string {
    if (loadedFonts.has(quillId)) {
        const entry = FONT_REGISTRY.get(quillId);
        if (entry) return entry.pdfmakeName;
    }
    // Standard fonts are always available
    const entry = FONT_REGISTRY.get(quillId);
    if (entry?.source === 'standard') return entry.pdfmakeName;
    return 'Roboto';
}

// ── Internal loading functions ───────────────────────────────────────────────

async function loadFont(entry: FontRegistryEntry): Promise<void> {
    if (entry.source === 'standard') {
        await loadStandardFont(entry);
    } else {
        await loadLocalFont(entry);
    }
    loadedFonts.add(entry.quillId);
}

/**
 * Load a standard PDF font from pdfmake's bundled modules.
 */
async function loadStandardFont(entry: FontRegistryEntry): Promise<void> {
    let mod: Record<string, unknown>;

    switch (entry.standardFont) {
        case 'Courier':
            mod = await import('pdfmake/build/standard-fonts/Courier');
            break;
        case 'Helvetica':
            mod = await import('pdfmake/build/standard-fonts/Helvetica');
            break;
        case 'Times':
            mod = await import('pdfmake/build/standard-fonts/Times');
            break;
        default:
            throw new Error(`Unknown standard font: ${entry.standardFont}`);
    }

    const fontContainer = (mod.default ?? mod) as { vfs: TVirtualFileSystem; fonts: TFontDictionary };
    const pdfMake = await getPdfMake();
    pdfMake.addVirtualFileSystem(fontContainer.vfs);
    pdfMake.addFonts(fontContainer.fonts);
}

/**
 * Load a local TTF font from /fonts/ttf/ and register it in pdfmake's VFS.
 *
 * For each variant (normal, bold, italics, bolditalics):
 *   1. Fetch the TTF file from the static server
 *   2. Convert to base64 for pdfmake's VFS
 *   3. Register the VFS entry and font dictionary
 */
async function loadLocalFont(entry: FontRegistryEntry): Promise<void> {
    if (!entry.ttfFiles) {
        throw new Error(`No TTF files configured for font: ${entry.quillId}`);
    }

    const vfs: Record<string, string> = {};
    const fontDef: Record<string, string> = {};
    const downloadCache = new Map<string, Promise<ArrayBuffer>>();

    const variantKeys = ['normal', 'bold', 'italics', 'bolditalics'] as const;

    const downloads = variantKeys.map(async (variant) => {
        const filename = entry.ttfFiles![variant];
        const vfsName = `${entry.pdfmakeName}-${variant}.ttf`;

        // Deduplicate: same file may serve multiple variants
        if (!downloadCache.has(filename)) {
            downloadCache.set(filename, fetchTtfBuffer(filename));
        }
        const buffer = await downloadCache.get(filename)!;
        vfs[vfsName] = await arrayBufferToBase64(buffer);
        fontDef[variant] = vfsName;

        // Cache cmap from the normal variant for font-coverage fallback
        if (variant === 'normal') {
            fontCmaps.set(entry.pdfmakeName, extractCmap(buffer));
        }
    });

    await Promise.all(downloads);

    const pdfMake = await getPdfMake();
    pdfMake.addVirtualFileSystem(vfs);
    pdfMake.addFonts({ [entry.pdfmakeName]: fontDef });
}

// ── Font coverage fallback ───────────────────────────────────────────────────

/**
 * Split text into segments based on font character coverage.
 *
 * Fallback chain for each character:
 *   1. Primary font (custom or Roboto) — use if it has the glyph
 *   2. Roboto — use if the primary font lacks it (skipped when primary IS Roboto)
 *   3. Noto Sans Symbols 2 — fallback for card suits, check marks, stars, etc.
 *   4. Primary font — best effort if nothing has the glyph
 *
 * Returns a single-element array when all characters are covered (common case).
 */
export function splitTextByFontCoverage(
    text: string,
    pdfmakeName: string,
): Array<{ text: string; font: string }> {
    const primaryCmap = fontCmaps.get(pdfmakeName);
    const robotoCmap = pdfmakeName !== 'Roboto' ? fontCmaps.get('Roboto') : undefined;
    const fallbackCmap = fontCmaps.get(FALLBACK_FONT);

    // No cmaps available at all — can't do any coverage checking
    if (!primaryCmap && !robotoCmap && !fallbackCmap) {
        return [{ text, font: pdfmakeName }];
    }

    const segments: Array<{ text: string; font: string }> = [];
    let currentText = '';
    let currentFont = pdfmakeName;

    for (const char of text) {
        const cp = char.codePointAt(0)!;

        let targetFont: string;
        if (primaryCmap?.has(cp)) {
            targetFont = pdfmakeName;
        } else if (robotoCmap?.has(cp)) {
            targetFont = 'Roboto';
        } else if (fallbackCmap?.has(cp)) {
            targetFont = FALLBACK_FONT;
        } else {
            targetFont = pdfmakeName; // best effort
        }

        if (targetFont !== currentFont && currentText) {
            segments.push({ text: currentText, font: currentFont });
            currentText = '';
        }
        currentText += char;
        currentFont = targetFont;
    }

    if (currentText) {
        segments.push({ text: currentText, font: currentFont });
    }

    return segments;
}

// ── Fallback font detection ──────────────────────────────────────────────

/**
 * Check if the HTML text contains characters not covered by Roboto
 * or any loaded custom font. If so, the fallback font should be loaded.
 *
 * Strips HTML tags and checks each unique codepoint against all loaded cmaps.
 */
function textNeedsFallbackFont(html: string): boolean {
    // Strip HTML tags to get just text content
    const text = html.replace(/<[^>]+>/g, '');
    if (!text) return false;

    const robotoCmap = fontCmaps.get('Roboto');

    // Collect all loaded font cmaps
    const allCmaps: Set<number>[] = [];
    if (robotoCmap) allCmaps.push(robotoCmap);
    for (const [name, cmap] of fontCmaps) {
        if (name !== 'Roboto') allCmaps.push(cmap);
    }

    // If we have no cmaps at all, we can't check — skip fallback
    if (allCmaps.length === 0) return false;

    for (const char of text) {
        const cp = char.codePointAt(0)!;
        // Skip ASCII and common Latin — always covered
        if (cp < 0x100) continue;
        // Check if any loaded font covers this codepoint
        if (!allCmaps.some(cmap => cmap.has(cp))) {
            return true;
        }
    }
    return false;
}

// ── Roboto cmap extraction ───────────────────────────────────────────────

/**
 * Extract Roboto's character map from pdfmake's bundled VFS.
 * Called once on first PDF generation so Roboto can participate in
 * the fallback chain (not just serve as a blind default).
 */
function ensureRobotoCmap(): Promise<void> {
    if (!robotoCmapPromise) {
        robotoCmapPromise = (async () => {
            try {
                const { pdfFonts } = await loadPdfMake();
                const robotoBase64 = (pdfFonts as Record<string, string>)['Roboto-Regular.ttf'];
                if (!robotoBase64) return;

                const buffer = base64ToArrayBuffer(robotoBase64);
                fontCmaps.set('Roboto', extractCmap(buffer));
            } catch (e) {
                console.warn('[fontLoader] Failed to extract Roboto cmap:', e);
            }
        })();
    }
    return robotoCmapPromise;
}

/**
 * Decode a base64 string into an ArrayBuffer.
 */
function base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
}

// ── TTF file fetching ────────────────────────────────────────────────────────

const TTF_LOCAL_PATH = '/fonts/ttf/';
const FONT_CDN_URL = 'https://outlaw-fonts.john-080.workers.dev/';
const FONT_CDN_KEY = import.meta.env.VITE_FONT_CDN_KEY as string | undefined;

/**
 * Fetch a TTF file as a raw ArrayBuffer.
 * Uses the Cloudflare R2 CDN when a key is configured, falls back to local.
 */
async function fetchTtfBuffer(filename: string): Promise<ArrayBuffer> {
    if (FONT_CDN_KEY) {
        const response = await fetch(FONT_CDN_URL + filename, {
            headers: { 'X-Font-Key': FONT_CDN_KEY },
        });
        if (response.ok) return response.arrayBuffer();
        console.warn(`[fontLoader] CDN fetch failed (${response.status}), falling back to local: ${filename}`);
    }

    const response = await fetch(TTF_LOCAL_PATH + filename);
    if (!response.ok) {
        throw new Error(`Failed to fetch font: ${response.status} ${filename}`);
    }
    return response.arrayBuffer();
}

/**
 * Convert an ArrayBuffer to a base64 string for pdfmake VFS.
 */
function arrayBufferToBase64(buffer: ArrayBuffer): Promise<string> {
    return new Promise<string>((resolve, reject) => {
        const blob = new Blob([buffer]);
        const reader = new FileReader();
        reader.onloadend = () => {
            const dataUrl = reader.result as string;
            const commaIndex = dataUrl.indexOf(',');
            if (commaIndex === -1) {
                reject(new Error('FileReader produced unexpected data URL format'));
                return;
            }
            resolve(dataUrl.substring(commaIndex + 1));
        };
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(blob);
    });
}

// ── TTF cmap extraction ─────────────────────────────────────────────────────

/**
 * Extract the character map (cmap) from a TrueType font buffer.
 * Returns a Set of Unicode codepoints that the font supports.
 *
 * Parses the TTF table directory to find the 'cmap' table, then reads
 * Format 12 (segmented coverage) or Format 4 (segment mapping) subtables.
 */
function extractCmap(buffer: ArrayBuffer): Set<number> {
    const view = new DataView(buffer);
    const numTables = view.getUint16(4);

    // Find 'cmap' table in the font directory
    let cmapOffset = 0;
    for (let i = 0; i < numTables; i++) {
        const entryOffset = 12 + i * 16;
        const tag = String.fromCharCode(
            view.getUint8(entryOffset),
            view.getUint8(entryOffset + 1),
            view.getUint8(entryOffset + 2),
            view.getUint8(entryOffset + 3),
        );
        if (tag === 'cmap') {
            cmapOffset = view.getUint32(entryOffset + 8);
            break;
        }
    }
    if (!cmapOffset) return new Set();

    // Read cmap header and find the best encoding subtable
    const numSubtables = view.getUint16(cmapOffset + 2);
    let bestOffset = 0;
    let bestFormat = 0;

    for (let i = 0; i < numSubtables; i++) {
        const rec = cmapOffset + 4 + i * 8;
        const platformID = view.getUint16(rec);
        const subtableOffset = cmapOffset + view.getUint32(rec + 4);
        const format = view.getUint16(subtableOffset);

        // Prefer Unicode platform (0 or 3) with highest format number
        if ((platformID === 0 || platformID === 3) && format >= bestFormat) {
            bestOffset = subtableOffset;
            bestFormat = format;
        }
    }

    const codepoints = new Set<number>();

    if (bestFormat === 12) {
        // Format 12: Segmented coverage — supports full Unicode (>= U+FFFF)
        const numGroups = view.getUint32(bestOffset + 12);
        for (let i = 0; i < numGroups; i++) {
            const base = bestOffset + 16 + i * 12;
            const startChar = view.getUint32(base);
            const endChar = view.getUint32(base + 4);
            for (let cp = startChar; cp <= endChar; cp++) {
                codepoints.add(cp);
            }
        }
    } else if (bestFormat === 4) {
        // Format 4: Segment mapping — BMP only (U+0000–U+FFFF)
        const segCount = view.getUint16(bestOffset + 6) / 2;
        const endCodesStart = bestOffset + 14;
        const startCodesStart = endCodesStart + segCount * 2 + 2; // +2 for reservedPad
        for (let i = 0; i < segCount; i++) {
            const endCode = view.getUint16(endCodesStart + i * 2);
            const startCode = view.getUint16(startCodesStart + i * 2);
            if (startCode === 0xFFFF) break;
            for (let cp = startCode; cp <= endCode; cp++) {
                codepoints.add(cp);
            }
        }
    }

    return codepoints;
}
